import * as React from 'react'
import { Loader2 } from 'lucide-react'
import {
  DEFAULT_LOW_CONFIDENCE_LIMIT,
  DEFAULT_LOW_CONFIDENCE_THRESHOLD,
  useActiveBranchRecord,
  useDeleteManualNodeMutation,
  useDeleteManualRelationshipMutation,
  useListLowConfidenceNodesQuery,
  useListLowConfidenceRelationshipsQuery,
  useListReviewQueueNodesQuery,
  useListReviewQueueRelationshipsQuery,
  useRecordNodeEvidenceMutation,
  useRecordRelationshipEvidenceMutation,
  type LowConfidenceNode,
  type LowConfidenceRelationship,
  type ReviewQueueNode,
  type ReviewQueueRelationship
} from '@axilon-apps/domains-ontology-ui-state'

import { useProtectedBranchGuard } from '../../../components/Branches/ProtectedBranchPromptDialog'
import { Badge } from '../../../components/ui/badge'
import { Card, CardContent } from '../../../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { formatRelativeTime } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import { ConfidenceValue } from '../../PlantPage/entity/ConfidenceValue'
import { PlantHref } from '../../PlantPage/PlantHref'
import { DEFAULT_ACCEPT_NOTE } from './EntityReviewRow'

const actionBtn =
  'inline-flex cursor-pointer items-center rounded-md border px-2 py-0.5 text-11 font-medium transition-colors duration-150 ease-out'
const acceptBtn = `${actionBtn} border-st-run/40 bg-st-run-bg text-st-run hover:bg-st-run-bg/70`
const deleteBtn = `${actionBtn} border-st-down/40 bg-st-down-bg text-st-down hover:bg-st-down-bg/70`
const cancelBtn = `${actionBtn} border-line bg-bg text-ink-2 hover:bg-bg-sunk`

const THRESHOLD_OPTIONS = [
  { value: '0.5', label: 'Below 50%', threshold: 0.5 },
  { value: '0.8', label: 'Below 80%', threshold: 0.8 },
  { value: '0.9', label: 'Below 90%', threshold: 0.9 },
  // Threshold 1.0 → confidence < 1.0 (everything not already accepted at 100%).
  { value: 'any', label: 'Any below 100%', threshold: 1 }
] as const

const LIMIT_OPTIONS = [25, 50, 100, 200] as const

/** Identity label stamped by the graph store — not useful as a row badge. */
function displayLabels(labels: string[]): string[] {
  return labels.filter((l) => l !== '_Entity')
}

type ReviewNode = LowConfidenceNode | ReviewQueueNode
type ReviewRelationship = LowConfidenceRelationship | ReviewQueueRelationship

function relationshipTitle(rel: ReviewRelationship): string {
  const source = rel.sourceName ?? rel.sourceId ?? '?'
  const target = rel.targetName ?? rel.targetId ?? '?'
  return `${source} → ${target}`
}

function ReviewReason({
  reason,
  flaggedBy,
  flaggedAt
}: {
  reason?: string
  flaggedBy?: string
  flaggedAt?: string
}): React.ReactElement | null {
  if (!reason) return null
  return (
    <div className="rounded-md border border-st-warn/30 bg-st-warn-bg/20 px-2.5 py-1.5">
      <p className="m-0 whitespace-pre-wrap text-xs text-ink-2">{reason}</p>
      {(flaggedBy ?? flaggedAt) ? (
        <p className="m-0 mt-0.5 text-10 uppercase tracking-wide text-ink-3">
          {flaggedBy ? `by ${flaggedBy}` : ''}
          {flaggedBy && flaggedAt ? ' · ' : ''}
          {flaggedAt ? formatRelativeTime(flaggedAt) : ''}
        </p>
      ) : null}
    </div>
  )
}

const plantLinkClass = 'min-w-0 truncate font-medium text-brand hover:underline'

function EndpointLink({ id, label }: { id?: string; label: string }): React.ReactElement {
  if (!id) {
    return (
      <span className="truncate font-medium text-ink" title={label}>
        {label}
      </span>
    )
  }
  return (
    <PlantHref id={id} title={label} className={plantLinkClass}>
      {label}
    </PlantHref>
  )
}

function AcceptComposer({
  note,
  onNoteChange,
  isSubmitting,
  submitError,
  onConfirm,
  onCancel
}: {
  note: string
  onNoteChange: (next: string) => void
  isSubmitting: boolean
  submitError: string
  onConfirm: () => void
  onCancel: () => void
}): React.ReactElement {
  const canConfirm = note.trim().length > 0 && !isSubmitting
  return (
    <div className="flex flex-col gap-2 rounded-md border border-st-run/40 bg-st-run-bg/20 p-2.5">
      <label className="text-11 font-medium uppercase tracking-wide text-st-run">
        Note recorded on the entity (required).
      </label>
      <textarea
        autoFocus
        rows={2}
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder={DEFAULT_ACCEPT_NOTE}
        className="w-full resize-y rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-st-run focus:outline-none focus:ring-2 focus:ring-st-run/30"
      />
      {submitError ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2.5 py-1.5 text-xs text-st-down">
          {submitError}
        </div>
      ) : null}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={!canConfirm}
          onClick={onConfirm}
          className={cn(
            actionBtn,
            canConfirm
              ? 'border-st-run bg-st-run font-semibold text-bg hover:opacity-90'
              : 'cursor-not-allowed border-line bg-bg-sunk text-ink-3'
          )}
        >
          {isSubmitting ? 'Accepting…' : 'Confirm accept'}
        </button>
        <button type="button" onClick={onCancel} className={cancelBtn}>
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * Settings panel for the lowest-confidence nodes and edges. Accept records
 * manual evidence (which syncs confidence to 1.0 server-side); Delete hard-
 * deletes the entity. Laid out as a dense table to match other Settings lists.
 */
export function LowConfidenceCard({
  embedded = false,
  mode = 'low-confidence'
}: {
  embedded?: boolean
  mode?: 'needs-review' | 'low-confidence'
} = {}) {
  const isReviewQueue = mode === 'needs-review'
  const defaultNote = isReviewQueue ? 'reviewed and confirmed correct' : DEFAULT_ACCEPT_NOTE
  const [thresholdKey, setThresholdKey] = React.useState('0.8')
  const [limit, setLimit] = React.useState<number>(DEFAULT_LOW_CONFIDENCE_LIMIT)
  const threshold =
    THRESHOLD_OPTIONS.find((o) => o.value === thresholdKey)?.threshold ??
    DEFAULT_LOW_CONFIDENCE_THRESHOLD

  const queryArgs = React.useMemo(() => ({ threshold, limit }), [threshold, limit])
  const lowNodesQuery = useListLowConfidenceNodesQuery(queryArgs, { skip: isReviewQueue })
  const lowRelsQuery = useListLowConfidenceRelationshipsQuery(queryArgs, { skip: isReviewQueue })
  const reviewNodesQuery = useListReviewQueueNodesQuery(undefined, { skip: !isReviewQueue })
  const reviewRelsQuery = useListReviewQueueRelationshipsQuery(undefined, { skip: !isReviewQueue })
  const nodesQuery = isReviewQueue ? reviewNodesQuery : lowNodesQuery
  const relsQuery = isReviewQueue ? reviewRelsQuery : lowRelsQuery
  const [recordNodeEvidence, nodeEvidenceState] = useRecordNodeEvidenceMutation()
  const [recordRelEvidence, relEvidenceState] = useRecordRelationshipEvidenceMutation()
  const [deleteNode, deleteNodeState] = useDeleteManualNodeMutation()
  const [deleteRel, deleteRelState] = useDeleteManualRelationshipMutation()
  const branchGuard = useProtectedBranchGuard(useActiveBranchRecord())

  const [composingKey, setComposingKey] = React.useState<string | null>(null)
  const [note, setNote] = React.useState(defaultNote)
  const [submitError, setSubmitError] = React.useState('')
  const [actionError, setActionError] = React.useState('')

  const closeComposer = () => {
    setComposingKey(null)
    setNote(defaultNote)
    setSubmitError('')
  }

  const openComposer = (key: string) => {
    setComposingKey(key)
    setNote(defaultNote)
    setSubmitError('')
    setActionError('')
  }

  const handleAcceptNode = (node: ReviewNode) => {
    const trimmed = note.trim()
    if (!trimmed || nodeEvidenceState.isLoading) return
    if (!branchGuard.ensureWritable()) return
    setSubmitError('')
    recordNodeEvidence({
      nodeId: node.id,
      note: trimmed,
      ...(isReviewQueue ? { needsReview: false } : {})
    })
      .unwrap()
      .then(closeComposer)
      .catch(() => setSubmitError(`Could not accept ${node.name ?? node.id}.`))
  }

  const handleAcceptRel = (rel: ReviewRelationship) => {
    const trimmed = note.trim()
    if (!trimmed || relEvidenceState.isLoading) return
    if (!branchGuard.ensureWritable()) return
    setSubmitError('')
    recordRelEvidence({
      relationshipId: rel.id,
      note: trimmed,
      ...(isReviewQueue ? { needsReview: false } : {})
    })
      .unwrap()
      .then(closeComposer)
      .catch(() => setSubmitError(`Could not accept ${relationshipTitle(rel)}.`))
  }

  const handleDeleteNode = (node: ReviewNode) => {
    if (!branchGuard.ensureWritable()) return
    const label = node.name ?? node.id
    if (!window.confirm(`Delete node "${label}"? This cannot be undone.`)) return
    setActionError('')
    deleteNode(node.id)
      .unwrap()
      .catch(() => setActionError(`Could not delete ${label}.`))
  }

  const handleDeleteRel = (rel: ReviewRelationship) => {
    if (!branchGuard.ensureWritable()) return
    const label = `${relationshipTitle(rel)} [${rel.type}]`
    if (!window.confirm(`Delete relationship "${label}"? This cannot be undone.`)) return
    setActionError('')
    deleteRel(rel.id)
      .unwrap()
      .catch(() => setActionError(`Could not delete ${label}.`))
  }

  const nodes: ReviewNode[] = nodesQuery.data ?? []
  const rels: ReviewRelationship[] = relsQuery.data ?? []
  const isBusy =
    nodeEvidenceState.isLoading ||
    relEvidenceState.isLoading ||
    deleteNodeState.isLoading ||
    deleteRelState.isLoading

  const filters = !isReviewQueue ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select value={thresholdKey} onValueChange={setThresholdKey}>
        <SelectTrigger
          size="sm"
          aria-label="Confidence threshold"
          className="h-7 w-[8.25rem] gap-1 px-2 text-xs shadow-none"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {THRESHOLD_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value} className="text-xs">
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={String(limit)} onValueChange={(next) => setLimit(Number.parseInt(next, 10))}>
        <SelectTrigger
          size="sm"
          aria-label="Result limit"
          className="h-7 w-[6.5rem] gap-1 px-2 text-xs shadow-none"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LIMIT_OPTIONS.map((n) => (
            <SelectItem key={n} value={String(n)} className="text-xs">
              Top {n}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null

  const content = (
    <div className="flex flex-col gap-3">
      {!embedded ? (
        <h3 className="text-sm font-semibold text-ink">
          {isReviewQueue ? 'Needs review' : 'Low confidence'}
        </h3>
      ) : null}
      <p className="m-0 text-xs leading-relaxed text-ink-3">
        {isReviewQueue
          ? 'Entities explicitly flagged for a human decision. Accept clears the review flag and confirms at 100%; delete removes the entity.'
          : 'Lowest-confidence nodes and edges below the threshold. Accept confirms at 100% with a note; delete removes the entity.'}
      </p>

      {actionError ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
          {actionError}
        </div>
      ) : null}

      <Tabs defaultValue="nodes" className="gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line">
          <TabsList variant="line" className="h-7 justify-start bg-transparent p-0">
            <TabsTrigger
              value="nodes"
              className="h-7 flex-none rounded-none px-2.5 text-xs after:bottom-[-1px]"
            >
              Nodes ({nodes.length})
            </TabsTrigger>
            <TabsTrigger
              value="edges"
              className="h-7 flex-none rounded-none px-2.5 text-xs after:bottom-[-1px]"
            >
              Edges ({rels.length})
            </TabsTrigger>
          </TabsList>
          {filters ? <div className="pb-1">{filters}</div> : null}
        </div>

        <TabsContent value="nodes" className="mt-0 flex flex-col gap-2">
          {nodesQuery.error ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              Failed to load {isReviewQueue ? 'nodes needing review' : 'low-confidence nodes'}.
            </div>
          ) : null}
          {nodesQuery.isLoading ? (
            <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
              <Loader2 className="size-3.5 animate-spin" /> Loading…
            </div>
          ) : nodes.length === 0 && !nodesQuery.error ? (
            <p className="m-0 py-1 text-xs text-ink-3">
              {isReviewQueue
                ? 'No nodes are awaiting review.'
                : 'No nodes below this confidence threshold.'}
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-line bg-bg-sunk text-11 uppercase tracking-[0.04em] text-ink-3">
                  <tr>
                    <th className="px-3 py-1.5 font-semibold">Entity</th>
                    <th className="px-3 py-1.5 font-semibold">Labels</th>
                    <th className="w-[4.5rem] px-3 py-1.5 font-semibold">Conf.</th>
                    <th className="w-[7.5rem] px-3 py-1.5 font-semibold">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {nodes.map((node) => {
                    const key = `node:${node.id}`
                    const isComposing = composingKey === key
                    const labels = displayLabels(node.labels)
                    const reviewNode = isReviewQueue ? (node as ReviewQueueNode) : undefined
                    return (
                      <React.Fragment key={key}>
                        <tr className="bg-card">
                          <td className="max-w-[18rem] px-3 py-1.5">
                            <div className="flex min-w-0 flex-col gap-0.5">
                              <PlantHref
                                id={node.id}
                                title={node.name ?? node.id}
                                className={plantLinkClass}
                              >
                                {node.name ?? node.id}
                              </PlantHref>
                              {node.path ? (
                                <span className="truncate text-11 text-ink-3" title={node.path}>
                                  {node.path}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-1.5">
                            <div className="flex flex-wrap gap-1">
                              {labels.map((label) => (
                                <Badge
                                  key={label}
                                  variant="outline"
                                  className="rounded-md px-1.5 py-0 text-10"
                                >
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-1.5">
                            <ConfidenceValue value={node.confidence} compact />
                          </td>
                          <td className="px-3 py-1.5">
                            {!isComposing ? (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  className={acceptBtn}
                                  onClick={() => openComposer(key)}
                                >
                                  Accept
                                </button>
                                <button
                                  type="button"
                                  className={deleteBtn}
                                  onClick={() => handleDeleteNode(node)}
                                >
                                  Delete
                                </button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                        {reviewNode?.reason ? (
                          <tr className="bg-card">
                            <td colSpan={4} className="px-3 pb-2 pt-0">
                              <ReviewReason
                                reason={reviewNode.reason}
                                flaggedBy={reviewNode.flagged_by}
                                flaggedAt={reviewNode.flagged_at}
                              />
                            </td>
                          </tr>
                        ) : null}
                        {isComposing ? (
                          <tr className="bg-card">
                            <td colSpan={4} className="px-3 py-2">
                              <AcceptComposer
                                note={note}
                                onNoteChange={setNote}
                                isSubmitting={isBusy && composingKey === key}
                                submitError={submitError}
                                onConfirm={() => handleAcceptNode(node)}
                                onCancel={closeComposer}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="edges" className="mt-0 flex flex-col gap-2">
          {relsQuery.error ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              Failed to load {isReviewQueue ? 'edges needing review' : 'low-confidence edges'}.
            </div>
          ) : null}
          {relsQuery.isLoading ? (
            <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
              <Loader2 className="size-3.5 animate-spin" /> Loading…
            </div>
          ) : rels.length === 0 && !relsQuery.error ? (
            <p className="m-0 py-1 text-xs text-ink-3">
              {isReviewQueue
                ? 'No edges are awaiting review.'
                : 'No edges below this confidence threshold.'}
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-line bg-bg-sunk text-11 uppercase tracking-[0.04em] text-ink-3">
                  <tr>
                    <th className="px-3 py-1.5 font-semibold">Relationship</th>
                    <th className="px-3 py-1.5 font-semibold">Type</th>
                    <th className="w-[4.5rem] px-3 py-1.5 font-semibold">Conf.</th>
                    <th className="w-[7.5rem] px-3 py-1.5 font-semibold">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rels.map((rel) => {
                    const key = `rel:${rel.id}`
                    const isComposing = composingKey === key
                    const title = relationshipTitle(rel)
                    const reviewRel = isReviewQueue ? (rel as ReviewQueueRelationship) : undefined
                    return (
                      <React.Fragment key={key}>
                        <tr className="bg-card">
                          <td className="max-w-[18rem] px-3 py-1.5">
                            <span className="flex min-w-0 items-center gap-1" title={title}>
                              <EndpointLink
                                id={rel.sourceId}
                                label={rel.sourceName ?? rel.sourceId ?? '?'}
                              />
                              <span className="shrink-0 text-ink-3">→</span>
                              <EndpointLink
                                id={rel.targetId}
                                label={rel.targetName ?? rel.targetId ?? '?'}
                              />
                            </span>
                          </td>
                          <td className="px-3 py-1.5">
                            <Badge variant="outline" className="rounded-md px-1.5 py-0 text-10">
                              {rel.type}
                            </Badge>
                          </td>
                          <td className="px-3 py-1.5">
                            <ConfidenceValue value={rel.confidence} compact />
                          </td>
                          <td className="px-3 py-1.5">
                            {!isComposing ? (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  className={acceptBtn}
                                  onClick={() => openComposer(key)}
                                >
                                  Accept
                                </button>
                                <button
                                  type="button"
                                  className={deleteBtn}
                                  onClick={() => handleDeleteRel(rel)}
                                >
                                  Delete
                                </button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                        {reviewRel?.reason ? (
                          <tr className="bg-card">
                            <td colSpan={4} className="px-3 pb-2 pt-0">
                              <ReviewReason
                                reason={reviewRel.reason}
                                flaggedBy={reviewRel.flagged_by}
                                flaggedAt={reviewRel.flagged_at}
                              />
                            </td>
                          </tr>
                        ) : null}
                        {isComposing ? (
                          <tr className="bg-card">
                            <td colSpan={4} className="px-3 py-2">
                              <AcceptComposer
                                note={note}
                                onNoteChange={setNote}
                                isSubmitting={isBusy && composingKey === key}
                                submitError={submitError}
                                onConfirm={() => handleAcceptRel(rel)}
                                onCancel={closeComposer}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )

  if (embedded) {
    return (
      <>
        {content}
        {branchGuard.dialog}
      </>
    )
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent>{content}</CardContent>
      {branchGuard.dialog}
    </Card>
  )
}
